import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import get from 'lodash/get';
import includes from 'lodash/includes';

import env from 'Env';
import Meta from 'Components/Utility/SEO/Meta';
import useViewport from 'Hooks/useViewport';
import FanFavouritesSection from 'Containers/HomePage/FanFavouritesSection';
import FeaturedCarouselContainer from 'Components/Carousel/FeaturedCarouselContainer';
import FlashDealsSection from 'Containers/AlgoliaSections/FlashDealsSection';
import MidPageBannerContainer from 'Components/Banner/MidPageBanner/MidPageBannerContainer';
import NonGameBanner from 'Components/Banner/NonGameBanner';
import ProductCarouselSection from 'Containers/HomePage/ProductCarouselSection';
import PublisherCarousel from 'Components/HomePage/PublisherCarousel';
import QuickLinks from 'Components/QuickLinks/QuickLinks';
import RecentlyViewedSection from 'Containers/HomePage/RecentlyViewedSection';
import TakeOver from 'Components/Banner/TakeOver';
import TopSellersTop from 'Components/HomePage/TopSellersTop';
import TrustBanner from 'Components/HomePage/TrustBanner';
import WishlistMostWantedSection from 'Containers/HomePage/WishlistMostWantedSection';
import MysteryBundleCardContainer from 'Containers/Cards/MysteryBundleCardContainer';
import EmailSignupPlacement from 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement';
import MoreThanJustGames from 'Components/HomePage/MoreThanJustGames';
import LimitedTimeDealsCarousel from 'Components/Carousel/LimitedTimeDealsCarousel';
import PopularCategories from 'Components/HomePage/PopularCategories';

const BundleFestCountdown = lazy(() => import(/* webpackChunkName: "BlogPosts" */ 'Components/Banner/BundleFestCountdown/BundleFestCountdown'));
const ProductFinder = lazy(() => import(/* webpackChunkName: "ProductFinder" */ 'Components/ProductFinder/ProductFinder'));

const messages = defineMessages({
  desc: {
    id: 'home.meta.desc',
    defaultMessage: 'Get massive savings on exclusive Steam bundles and great PC, Mac, and Linux games - updated daily. Join our community of over 2.6 million gamers - explore Fanatical now!',
  },
  title: {
    id: 'home.meta.title',
    defaultMessage: 'Buy PC Games, Steam Keys, Game Bundles',
  },
  new_releases: {
    id: 'homepage.title.newreleases',
    defaultMessage: 'New Releases',
  },
  trending_deals: {
    id: 'homepage.title.trendingdeals',
    defaultMessage: 'Popular Games',
  },
  coming_soon: {
    id: 'homepage.title.comingsoon',
    defaultMessage: 'Upcoming Games',
  },
  newandupcoming: {
    id: 'homepage.title.newandupcoming',
    defaultMessage: 'New and Upcoming Releases',
  },
  upcoming_games: {
    id: 'homepage.title.upcominggames',
    defaultMessage: 'Upcoming Games',
  },
  most_gifted: {
    id: 'homepage.title.mostgifted',
    defaultMessage: 'Most Gifted',
  },
  bundles: {
    id: 'homepage.title.bundles',
    defaultMessage: 'More Game Bundles',
  },
});

const HomePage = ({ apiResults, siteBrand, userLibrary, steamLibrary, starDeal }) => {
  const { isAboveSm, isAboveMd, isBelowLg } = useViewport();
  const { formatMessage } = useIntl();

  const liveSales = useSelector(state => state.sales.liveSales);
  const isSaleLive = !!liveSales.find(sale => sale.majorSale);

  let topSellers = get(apiResults, 'topSellers', []);
  // const moreThanGames = get(apiResults, 'moreThanGames', []); Not used, remove during homepage clean up - JH
  const newReleaseHits = get(apiResults, 'newReleases', []);
  const trendingHits = get(apiResults, 'trendingDeals', []);
  const mostWishedHits = get(apiResults, 'mostWished', []);
  const gameBundles = get(apiResults, 'gameBundles', []);
  const mysteryBundles = get(apiResults, 'mysteryBundles', []);
  const fantasyVerseHits = get(apiResults, 'fantasyverse', []);
  const gameHits = get(apiResults, 'games', []);
  const booksComicsHits = get(apiResults, 'booksComics', []);
  const otherHits = get(apiResults, 'other', []);
  const booksStandardHits = get(apiResults, 'booksStandard', []);
  const softwareHits = get(apiResults, 'software', []);
  const elearningHits = get(apiResults, 'elearning', []);
  const audioHits = get(apiResults, 'audio', []);
  const giveaways = get(apiResults, 'giveaways', []);
  const comingSoonHits = get(apiResults, 'comingSoon', []);
  const gamesUnderFiveHits = get(apiResults, 'gamesUnderFive', []);

  // Combine fanatical owned and steam owned
  const purchaseHistoryNames = [...userLibrary, ...steamLibrary];

  // stops the test launching for no reason
  // we should only launch the test if the user owns
  // one of the first 11 products that we display on the homepage
  const hasPurchased = !!topSellers
    .slice(0, 11)
    .find(hit => purchaseHistoryNames.includes(hit.name));

  const shiftPurchasedTopSellersToBack = (hits) => {
    const unPurchasedTopSellers = hits.filter(hit => !purchaseHistoryNames.includes(hit.name));
    const purchasedTopSellers = hits.filter(hit => purchaseHistoryNames.includes(hit.name));

    return [...unPurchasedTopSellers, ...purchasedTopSellers];
  };

  if (hasPurchased) {
    topSellers = shiftPurchasedTopSellersToBack(topSellers);
  }

  const topSellerSlugs = [];
  topSellers.slice(0, 11).forEach((hit) => { topSellerSlugs.push(hit.slug); });

  // Get game bundles not in the top sellers
  const moreGameBundles = gameBundles.filter(hit => !includes(topSellerSlugs, hit.slug));

  const showLimitedTimeDeals = false;

  return (
    <React.Fragment>
      <Meta
        url="/"
        hits={topSellers}
        title={`Fanatical | ${formatMessage(messages.title)}`}
        description={formatMessage(messages.desc)}
        pageType="CollectionPage"
      />
      <TakeOver appearsOn="home" />

      <main className="homepage HomePage">
        <section className="container">
          <FeaturedCarouselContainer />
        </section>

        {isAboveSm &&
          <TrustBanner />
        }

        <FlashDealsSection noPadding useTimer threeProductTemplate />

        <BundleFestCountdown />

        <MysteryBundleCardContainer />

        <TopSellersTop
          hits={topSellers}
          gameHits={gameHits}
          gameBundles={gameBundles}
          booksComicsHits={booksComicsHits}
          otherHits={otherHits}
        />

        {showLimitedTimeDeals &&
          <LimitedTimeDealsCarousel
            gameHits={gameHits}
            starDeal={starDeal}
            gameBundles={gameBundles}
            mysteryBundles={mysteryBundles}
            giveaways={giveaways}
          />
        }

        {isAboveMd &&
          <ProductCarouselSection
            title={formatMessage(messages.bundles)}
            viewAllLocation="bundle"
            loadedHits={moreGameBundles}
            viewAllTitle={formatMessage(messages.bundles)}
            hitCardNameTag="h3"
            slidesToShow={3}
            lgSize={4}
          />
        }

        <EmailSignupPlacement needsContainer appearsOn="home" />

        <MoreThanJustGames
          fantasyVerseHits={fantasyVerseHits}
          booksStandardHits={booksStandardHits}
          softwareHits={softwareHits}
          elearningHits={elearningHits}
          audioHits={audioHits}
        />

        <MidPageBannerContainer appearsOn="home" />

        {isBelowLg &&
          <ProductCarouselSection
            newCarousel
            title={formatMessage(messages.newandupcoming)}
            viewAllLocation="upcoming-games"
            loadedHits={newReleaseHits}
            viewAllTitle={formatMessage(messages.newandupcoming)}
            hitCardNameTag="h3"
          />
        }

        <QuickLinks />

        {isAboveMd &&
          <PopularCategories
            trendingHits={trendingHits}
            newReleaseHits={newReleaseHits}
            mostWishedHits={mostWishedHits}
            comingSoonHits={comingSoonHits}
            gamesUnderFiveHits={gamesUnderFiveHits}
          />
        }

        <NonGameBanner />

        {isBelowLg &&
          <ProductCarouselSection
            newCarousel
            title={formatMessage(messages.trending_deals)}
            viewAllLocation="trending-deals"
            loadedHits={trendingHits}
            viewAllTitle={formatMessage(messages.trending_deals)}
            hitCardNameTag="h3"
          />
        }

        <ProductFinder />

        {env.homePublishers &&
          <PublisherCarousel showDealNumbers />
        }

        <FanFavouritesSection />

        {isBelowLg && siteBrand === 'fanatical' && isSaleLive &&
          <WishlistMostWantedSection loadedHits={mostWishedHits} />
        }

        {env.homeRecentlyViewed &&
          <RecentlyViewedSection hitCardNameTag="h3" />
        }
      </main>
    </React.Fragment>
  );
};

HomePage.propTypes = {
  siteBrand: PropTypes.string,
  flashSale: PropTypes.shape({}),
  apiResults: PropTypes.shape({}),
  userLibrary: PropTypes.arrayOf(PropTypes.string),
  steamLibrary: PropTypes.arrayOf(PropTypes.string),
};

HomePage.defaultProps = {
  dealsLive: 0,
  siteBrand: 'fanatical',
  flashSale: {
    upcomingDeals: [],
    currentDeals: [],
    nextDeal: 0,
  },
  apiResults: {},
  userLibrary: [],
  steamLibrary: [],
  starDeal: null,
};

const mapStateToProps = ({
  auth: { authenticated },
  flashSale,
  siteBrand,
  userLibrary,
  steamLibrary,
  starDeal,
}) => ({
  authenticated,
  flashSale,
  siteBrand,
  userLibrary,
  steamLibrary,
  starDeal,
});

export default connect(mapStateToProps)(HomePage);
